import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ArrowRight, TrendingUp, ArrowLeft } from "lucide-react";
import { SEOHead } from "@/features/shared/SEOHead";
import { Link } from "react-router-dom";
import { type LucideIcon } from "lucide-react";
import { NeedACarHighlight } from "@/components/case-studies/NeedACarHighlight";

export interface CaseStudyStat {
  value: string;
  label: string;
}

export interface CaseStudySection {
  title: string;
  content: string;
  bullets?: string[];
}

export interface CaseStudyData {
  // SEO
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonical: string;
  breadcrumbs?: Array<{ name: string; item: string }>;

  // Hero
  industry: string;
  industryColor: string; // e.g. "blue", "orange", "green", "purple", "red", "amber"
  clientName: string;
  headline: React.ReactNode;
  stats: CaseStudyStat[];

  // Sidebar
  client: string;
  industryLabel: string;
  location: string;
  services: string;
  website?: string;
  sidebarCTA?: { label: string; href: string };

  // Content sections
  sections: CaseStudySection[];

  // Results block
  results: { icon?: LucideIcon; label: string; sublabel: string }[];
  testimonial?: { quote: string; name: string; title: string; initials: string };

  // Stack
  stack?: string[];

  // Optional product/backend screenshots
  gallery?: { src: string; alt: string; caption?: string }[];
}

const colorMap: Record<string, { badge: string; stat: string; heroAccent: string }> = {
  blue: { badge: "bg-blue-500/20 text-blue-300 border-blue-500/50", stat: "text-blue-400", heroAccent: "text-blue-400" },
  orange: { badge: "bg-orange-500/20 text-orange-300 border-orange-500/50", stat: "text-orange-400", heroAccent: "text-orange-400" },
  green: { badge: "bg-green-500/20 text-green-300 border-green-500/50", stat: "text-green-400", heroAccent: "text-green-400" },
  purple: { badge: "bg-purple-500/20 text-purple-300 border-purple-500/50", stat: "text-purple-400", heroAccent: "text-purple-400" },
  red: { badge: "bg-red-500/20 text-red-300 border-red-500/50", stat: "text-red-400", heroAccent: "text-red-400" },
  amber: { badge: "bg-amber-500/20 text-amber-300 border-amber-500/50", stat: "text-amber-400", heroAccent: "text-amber-400" },
  teal: { badge: "bg-teal-500/20 text-teal-300 border-teal-500/50", stat: "text-teal-400", heroAccent: "text-teal-400" },
  rose: { badge: "bg-rose-500/20 text-rose-300 border-rose-500/50", stat: "text-rose-400", heroAccent: "text-rose-400" },
};

export function CaseStudyPage({ data }: { data: CaseStudyData }) {
  const colors = colorMap[data.industryColor] || colorMap.blue;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title={data.seoTitle}
        description={data.seoDescription}
        keywords={data.seoKeywords}
        ogTitle={data.seoTitle}
        ogDescription={data.seoDescription}
        canonical={data.canonical}
        robots="index, follow"
        breadcrumbs={data.breadcrumbs}
      />

      <main className="pt-40 pb-16">
        {/* Hero */}
        <section className="bg-slate-900 text-white py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <Link to="/case-studies/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors group">
                <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Back to Case Studies
              </Link>
              <div className="flex items-center gap-2 mb-6">
                <Badge className={colors.badge}>{data.industry}</Badge>
                <span className="text-slate-400">/</span>
                <span className="text-slate-300">{data.clientName}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {data.headline}
              </h1>
              {data.stats.length > 0 && (
                <div className={`grid grid-cols-2 md:grid-cols-${Math.min(data.stats.length, 4)} gap-6 mt-12 border-t border-white/10 pt-8`}>
                  {data.stats.map((stat, i) => (
                    <div key={i}>
                      <div className={`text-3xl font-bold ${colors.stat}`}>{stat.value}</div>
                      <div className="text-sm text-slate-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar */}
            <aside className="lg:w-1/4">
              <div className="sticky top-24 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Client Profile</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Client</div>
                      <div className="font-semibold">{data.client}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Industry</div>
                      <div className="font-semibold">{data.industryLabel}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Location</div>
                      <div className="font-semibold">{data.location}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Services</div>
                      <div className="font-semibold">{data.services}</div>
                    </div>
                    {data.website && (
                      <div>
                        <div className="text-sm text-muted-foreground">Website</div>
                        <a href={`https://${data.website}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">
                          {data.website}
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
                {data.sidebarCTA && (
                  <Button className="w-full" size="lg" asChild>
                    <Link to={data.sidebarCTA.href}>{data.sidebarCTA.label}</Link>
                  </Button>
                )}
                <Button className="w-full" variant="outline" size="lg" asChild>
                  <Link to="/case-studies/">← All Case Studies</Link>
                </Button>
              </div>
            </aside>

            {/* Content */}
            <article className="lg:w-3/4 prose prose-lg max-w-none prose-headings:font-bold prose-a:text-blue-600">
              {data.sections.map((section, i) => (
                <div key={i}>
                  <h2>{section.title}</h2>
                  <p>{section.content}</p>
                  {section.bullets && section.bullets.length > 0 && (
                    <ul>
                      {section.bullets.map((bullet, j) => (
                        <li key={j} dangerouslySetInnerHTML={{ __html: bullet }} />
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* Product / backend screenshots */}
              {data.gallery && data.gallery.length > 0 && (
                <div className="my-12 not-prose">
                  <h3 className="text-2xl font-bold mb-6">Inside the system</h3>
                  <div className="space-y-6">
                    {data.gallery.map((g, i) => (
                      <figure key={i} className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                        <img src={g.src} alt={g.alt} className="w-full" loading="lazy" />
                        {g.caption && (
                          <figcaption className="px-4 py-3 text-sm text-muted-foreground bg-slate-50 border-t border-slate-200">{g.caption}</figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                </div>
              )}

              {/* Results Block */}
              <div className="my-12 p-8 bg-slate-50 rounded-xl border border-slate-200 not-prose">
                <h3 className="text-2xl font-bold mb-4">The Impact</h3>
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {data.results.map((result, i) => {
                    const Icon = result.icon || TrendingUp;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <Icon className="w-5 h-5 text-green-600 mt-1" />
                        <div>
                          <div className="font-bold text-xl">{result.label}</div>
                          <div className="text-muted-foreground text-sm">{result.sublabel}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {data.testimonial && (
                  <>
                    <p className="text-lg italic mb-6">"{data.testimonial.quote}"</p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-xs">
                        {data.testimonial.initials}
                      </div>
                      <div>
                        <div className="font-bold">{data.testimonial.name}</div>
                        <div className="text-sm text-muted-foreground">{data.testimonial.title}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Stack */}
              {data.stack && data.stack.length > 0 && (
                <div className="my-8 not-prose">
                  <h3 className="text-xl font-bold mb-4">Technology Stack</h3>
                  <div className="flex flex-wrap gap-2">
                    {data.stack.map((tech, i) => (
                      <Badge key={i} variant="secondary" className="text-sm py-1 px-3">{tech}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Cross-link: flagship case study */}
              {!data.canonical.includes("need-a-car") && (
                <div className="my-10 not-prose">
                  <NeedACarHighlight focus="general" variant="compact" theme="slate" />
                </div>
              )}

              {/* CTA */}
              <div className="my-12 p-8 bg-primary/5 rounded-xl border border-primary/10 not-prose text-center">
                <h3 className="text-2xl font-bold mb-3">Install a similar system for your business</h3>
                <p className="text-muted-foreground mb-6">
                  We apply the same proven frameworks to every business we work with.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <Link to="/book-consultation/">Book Your Strategy Call <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/case-studies/">View More Case Studies</Link>
                  </Button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>

      <Footer />
      <StickyCTA />
    </div>
  );
}
